import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Send } from "lucide-react";

import {
  arriveOnMyWay,
  cancelOnMyWay,
  checkIn as postCheckIn,
  endCheckIn,
  fetchActiveOnMyWay,
  fetchVenuePresence,
  startOnMyWay,
  updateOnMyWayEta,
} from "../Services/SocialService";
import { getUserLocation } from "../Services/LocationService";
import { useAuth } from "../Services/AuthContext";
import { clockTime, formatInitials, timeAgo, timeLeft } from "../utils/format";
import type {
  CheckInVisibility,
  OnMyWay,
  VenuePresence,
} from "../data/types";
import CheckInPrivacy from "./Presence/CheckInPrivacy";
import EtaPicker from "./Presence/EtaPicker";
import OnMyWayCard from "./Presence/OnMyWayCard";
import "../Styles/VenueCheckIn.css";
import "../Styles/Presence.css";

/**
 * Kärnloopen på ett ställe: vem är här, vem är på väg, och vad kan du göra.
 *
 * Servern är auktoritativ. Klienten frågar om presence och ritar svaret —
 * den räknar aldrig ut själv vem som får se vad, och den hittar aldrig på
 * en check-in som backend inte skapat. Efter varje åtgärd läses tillståndet
 * om, så det som visas är det som faktiskt finns.
 */
type Props = {
  venueId: string;
  venueName: string;
};

const ERROR_TEXT: Record<string, string> = {
  too_far_away: "You need to be closer to check in here.",
  coords_required: "Turn on location to check in.",
  no_valid_recipients: "Pick at least one follower to share with.",
  eta_out_of_range: "Pick an arrival time between 1 and 180 minutes.",
  not_allowed: "You can't join this check-in.",
  on_my_way_expired: "That trip expired. Start a new one.",
  on_my_way_not_active: "That trip is no longer active.",
};

function parseError(e: unknown, fallback: string) {
  const message = e instanceof Error ? e.message : "";

  for (const key of Object.keys(ERROR_TEXT)) {
    if (message.includes(key)) return ERROR_TEXT[key];
  }

  return fallback;
}

export default function VenueCheckIn({ venueId, venueName }: Props) {
  const { isAuthenticated, user } = useAuth();

  const [presence, setPresence] = useState<VenuePresence | null>(null);
  const [tripElsewhere, setTripElsewhere] = useState<OnMyWay | null>(null);

  // En enda lås-flagga för alla åtgärder. Det som gör dubbeltryck ofarligt
  // är i sista hand serverns idempotens, men UI:t ska inte ens fråga två
  // gånger — och två parallella anrop kan landa i fel ordning.
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [visibility, setVisibility] = useState<CheckInVisibility>("FOLLOWERS");
  const [recipientIds, setRecipientIds] = useState<string[]>([]);
  const [etaOpenFor, setEtaOpenFor] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const [presenceRes, activeTrip] = await Promise.all([
        fetchVenuePresence(venueId),
        fetchActiveOnMyWay(),
      ]);

      setPresence(presenceRes);

      // En resa mot ett annat ställe hör inte hemma i det här kortet, men
      // användaren ska veta att en ny ersätter den.
      const trip = activeTrip.onMyWay;
      setTripElsewhere(trip && trip.venueId !== venueId ? trip : null);
    } catch (e) {
      console.error("venue presence fetch failed", e);
    }
  }, [venueId, isAuthenticated]);

  useEffect(() => {
    load();
  }, [load]);

  if (!isAuthenticated) return null;

  const myCheckIn = presence?.viewer.checkIn ?? null;
  const myTrip = presence?.viewer.onMyWay ?? null;
  const here = presence?.checkedIn ?? [];
  const onTheWay = presence?.onMyWay ?? [];

  /** Kör en åtgärd med lås, felhantering och omläsning. */
  const run = async (action: () => Promise<unknown>, fallback: string) => {
    if (isBusy) return;

    setIsBusy(true);
    setError(null);

    try {
      await action();
      await load();
    } catch (e) {
      console.error("presence action failed", e);
      setError(parseError(e, fallback));
    } finally {
      setIsBusy(false);
    }
  };

  const handleCheckIn = () =>
    run(async () => {
      // Position är frivillig — servern avgör om den krävs. Nekad GPS ska
      // inte blockera incheckningen.
      const location = await getUserLocation().catch(() => null);

      await postCheckIn({
        venueId,
        ...(location ? { lat: location.lat, lon: location.lon } : {}),
        visibility,
        ...(visibility === "SELECTED" ? { recipientIds } : {}),
      });
    }, "Could not check in right now.");

  const handleCheckOut = () => run(() => endCheckIn(), "Could not check out.");

  const handleStartTrip = (checkInId: string | null, etaMinutes: number) => {
    setEtaOpenFor(null);

    return run(
      () =>
        startOnMyWay({
          ...(checkInId ? { checkInId } : { venueId }),
          etaMinutes,
        }),
      "Could not start the trip."
    );
  };

  const handleArrive = () =>
    run(async () => {
      if (!myTrip) return;

      const location = await getUserLocation().catch(() => null);

      // Svaret bär check-in:en backend skapade. Vi ritar den efter
      // omläsningen i stället för att gissa fram en egen.
      await arriveOnMyWay(myTrip.id, {
        ...(location ? { lat: location.lat, lon: location.lon } : {}),
      });
    }, "Could not confirm you're here.");

  return (
    <div className="venue-checkin">
      <div className="venue-checkin-top">
        {myCheckIn ? (
          <>
            <button
              type="button"
              className="venue-checkin-button is-active"
              disabled={isBusy}
              onClick={handleCheckOut}
            >
              <MapPin size={15} strokeWidth={2.2} />
              {isBusy ? "…" : "Check out"}
            </button>

            <span className="venue-checkin-hint">
              {myCheckIn.visibility === "SELECTED"
                ? "Shared with the people you picked"
                : "Your followers can see you here"}
              {timeLeft(myCheckIn.expiresAt)
                ? ` · ${timeLeft(myCheckIn.expiresAt)} left`
                : ""}
            </span>
          </>
        ) : (
          <button
            type="button"
            className="venue-checkin-button"
            disabled={isBusy}
            onClick={handleCheckIn}
          >
            <MapPin size={15} strokeWidth={2.2} />
            {isBusy ? "…" : `Check in at ${venueName}`}
          </button>
        )}
      </div>

      {/* Integritetsvalet visas innan man checkar in — efteråt är det
          bestämt, och att låtsas annat vore missvisande. */}
      {!myCheckIn && user?.id && (
        <CheckInPrivacy
          userId={user.id}
          visibility={visibility}
          recipientIds={recipientIds}
          onVisibilityChange={setVisibility}
          onRecipientsChange={setRecipientIds}
        />
      )}

      {error && <p className="venue-checkin-error">{error}</p>}

      {myTrip ? (
        <OnMyWayCard
          onMyWay={myTrip}
          venueName={venueName}
          busy={isBusy}
          onChangeEta={(minutes) =>
            run(
              () => updateOnMyWayEta(myTrip.id, minutes),
              "Could not change the arrival time."
            )
          }
          onCancel={() =>
            run(() => cancelOnMyWay(myTrip.id), "Could not cancel the trip.")
          }
          onArrive={handleArrive}
        />
      ) : (
        !myCheckIn && (
          <div className="presence-block">
            {tripElsewhere && (
              <p className="presence-hint">
                You're already on the way somewhere else. Starting a new trip
                replaces it.
              </p>
            )}

            {etaOpenFor === "venue" ? (
              <EtaPicker
                label="When will you get here?"
                disabled={isBusy}
                onPick={(minutes) => handleStartTrip(null, minutes)}
              />
            ) : (
              <button
                type="button"
                className="presence-ghost"
                disabled={isBusy}
                onClick={() => setEtaOpenFor("venue")}
              >
                <Send size={13} strokeWidth={2.2} />
                I'm on my way here
              </button>
            )}
          </div>
        )
      )}

      {here.length > 0 && (
        <div className="venue-checkin-people" data-testid="presence-here">
          <p className="venue-checkin-label">Here right now</p>

          {here.map((c) => (
            <div key={c.id} className="venue-checkin-person">
              <Link
                to={`/profile/${c.user.id}`}
                className="venue-checkin-person-main"
              >
                <div className="venue-checkin-avatar">
                  {c.user.avatarUrl ? (
                    <img src={c.user.avatarUrl} alt="" />
                  ) : (
                    <span>{formatInitials(c.user.displayName)}</span>
                  )}
                </div>

                <div className="venue-checkin-person-text">
                  <strong>{c.isSelf ? "You" : c.user.displayName}</strong>
                  <small>{timeAgo(c.createdAt)}</small>
                </div>
              </Link>

              {/* Man kan inte vara på väg till ett ställe man redan står på,
                  och inte heller svara på sin egen check-in. */}
              {!c.isSelf && !myCheckIn && !myTrip && (
                etaOpenFor === c.id ? (
                  <EtaPicker
                    disabled={isBusy}
                    onPick={(minutes) => handleStartTrip(c.id, minutes)}
                  />
                ) : (
                  <button
                    type="button"
                    className="venue-checkin-ping"
                    disabled={isBusy}
                    onClick={() => setEtaOpenFor(c.id)}
                  >
                    <Send size={13} strokeWidth={2.2} />
                    On my way
                  </button>
                )
              )}
            </div>
          ))}
        </div>
      )}

      {onTheWay.length > 0 && (
        <div className="venue-checkin-people" data-testid="presence-on-the-way">
          <p className="venue-checkin-label">On the way</p>

          {onTheWay.map((o) => (
            <div key={o.id} className="venue-checkin-person">
              <Link
                to={`/profile/${o.user.id}`}
                className="venue-checkin-person-main"
              >
                <div className="venue-checkin-avatar">
                  {o.user.avatarUrl ? (
                    <img src={o.user.avatarUrl} alt="" />
                  ) : (
                    <span>{formatInitials(o.user.displayName)}</span>
                  )}
                </div>

                <div className="venue-checkin-person-text">
                  <strong>{o.isSelf ? "You" : o.user.displayName}</strong>
                  <small>
                    {timeLeft(o.etaAt)
                      ? `arriving around ${clockTime(o.etaAt)}`
                      : `expected around ${clockTime(o.etaAt)}`}
                  </small>
                </div>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
